/* eslint-disable react/display-name */
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Checkbox, Space, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, DesktopOutlined, EditOutlined } from '@ant-design/icons';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { useParams } from 'react-router-dom';
import tileStyles from 'src/styles/tile.module.css';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { CSS } from '@dnd-kit/utilities';
import { useDeviceGroupScreensById } from 'src/features/deviceGroups/deviceGroups.hooks';
import { updateDeviceGroup } from 'src/features/deviceGroups/deviceGroups.state';
import { AvailableScreens, DeviceScreen } from 'src/features/companies/companies.types';
import ConfirmButton from 'src/components/atoms/ConfirmButton';

const SortableItem: React.FC<PropsWithChildren<{ id: string; key: string }>> = props => {
  const { children } = props;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as React.CSSProperties;
  return (
    <div style={style} ref={setNodeRef} {...attributes} {...listeners} className={tileStyles.listTile}>
      {children}
    </div>
  );
};

const DeviceGroupScreensTab: React.FC = () => {
  const { groupId } = useParams() as { groupId: string };
  const screens = useDeviceGroupScreensById(groupId);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editScreenMode, setEditScreenMode] = useState<DeviceScreen | undefined>(undefined);
  const dispatch: AppDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleRemove = useCallback(
    async (screen: DeviceScreen) => {
      setIsLoading(true);
      await dispatch(
        updateDeviceGroup({ groupId, data: { deviceScreens: screens.filter(s => s.type !== screen.type) } })
      );
      setIsLoading(false);
    },
    [groupId, dispatch, screens]
  );
  const handleAdd = useCallback(
    async (screen: DeviceScreen) => {
      setIsLoading(true);
      await dispatch(updateDeviceGroup({ groupId, data: { deviceScreens: [...screens, screen] } }));
      setIsLoading(false);
    },
    [groupId, dispatch, screens]
  );

  const handleUpdate = useCallback(
    async (screen: DeviceScreen) => {
      setIsLoading(true);
      console.log(screen);
      console.log(screens.map(s => (s.type === screen.type ? screen : s)));
      await dispatch(
        updateDeviceGroup({
          groupId,
          data: { deviceScreens: screens.map(s => (s.type === screen.type ? screen : s)) },
        })
      );
      setIsLoading(false);
    },
    [groupId, dispatch, screens]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = screens.findIndex((i: DeviceScreen) => i.type === active.id);
        const newIndex = screens.findIndex((i: DeviceScreen) => i.type === over?.id);
        const result = arrayMove(screens, oldIndex, newIndex);
        setIsLoading(true);
        await dispatch(updateDeviceGroup({ groupId, data: { deviceScreens: result } }));
        setIsLoading(false);
      }
    },
    [dispatch, groupId, screens]
  );
  const getScreenName = (screen: string) => {
    switch (screen) {
      case 'vr_world':
        return 'Мир VR';
      case 'games':
        return 'Игры';
      case 'online_theater':
        return 'Онлайн кинотеатр';
      case 'kids':
        return 'Детям  Онлайн';
      case 'adult':
        return '18+';
      case 'casino':
        return 'Казино';
      case 'hotel_services':
        return 'Услуги отеля';
      default:
        return screen;
    }
  };

  const isScreenInCompany = useCallback((type: string) => screens.find(s => s.type === type), [screens]);

  const screenActions = useCallback(
    (screen: DeviceScreen) => {
      const isInCompany = isScreenInCompany(screen.type);
      if (isInCompany) {
        return (
          <ConfirmButton danger size='small' onClick={() => handleRemove(screen)}>
            {isInCompany ? 'Удалить' : 'Добавить'}
          </ConfirmButton>
        );
      }
      return (
        <Button size='small' onClick={() => handleAdd(screen)}>
          {isInCompany ? 'Удалить' : 'Добавить'}
        </Button>
      );
    },
    [handleAdd, handleRemove, isScreenInCompany]
  );

  const currentScreens = useMemo(() => {
    return editMode ? AvailableScreens : screens;
  }, [editMode, screens]);

  const renderListItem = (devScr: DeviceScreen) => {
    const screen = editScreenMode?.type === devScr.type ? editScreenMode : devScr;
    return (
      <SortableItem key={screen.type} id={screen.type}>
        <Space>
          <DesktopOutlined />
          {getScreenName(screen.type)}
        </Space>
        <Space>{editMode && screenActions(screen)}</Space>
        {!editMode && (
          <Space size={10}>
            <Space size={0}>
              <Checkbox
                checked={screen.online}
                onChange={e => setEditScreenMode({ ...(editScreenMode as DeviceScreen), online: e.target.checked })}
                disabled={editScreenMode?.type !== devScr.type}
              >
                Online
              </Checkbox>
              <Checkbox
                checked={screen.offline}
                onChange={e => setEditScreenMode({ ...(editScreenMode as DeviceScreen), offline: e.target.checked })}
                disabled={editScreenMode?.type !== devScr.type}
              >
                Offline
              </Checkbox>
            </Space>
            {editScreenMode?.type !== devScr.type ? (
              <Button onClick={() => setEditScreenMode(screen)}>
                <EditOutlined />
              </Button>
            ) : (
              <Space size={3}>
                <Button
                  type={'primary'}
                  onClick={async () => {
                    await handleUpdate(editScreenMode);
                    setEditScreenMode(undefined);
                  }}
                >
                  <CheckOutlined />
                </Button>
                <Button onClick={() => setEditScreenMode(undefined)}>
                  <CloseOutlined />
                </Button>
              </Space>
            )}
          </Space>
        )}
      </SortableItem>
    );
  };
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Экраны</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}>
          <Button onClick={() => setEditMode(!editMode)}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
        </ComponentRoleProtected>
      </div>
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={screens.map(s => s.type)} strategy={verticalListSortingStrategy}>
              {currentScreens.map(c => renderListItem(c))}
            </SortableContext>
          </DndContext>
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default DeviceGroupScreensTab;
